import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Collapse,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Layout,
  List,
  message,
  Modal,
  Progress,
  Result,
  Select,
  Space,
  Spin,
  Statistic,
  Steps,
  Switch,
  Tag,
  Typography,
} from 'antd'
import {
  CheckCircleOutlined,
  CloudDownloadOutlined,
  LockOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  RobotOutlined,
} from '@ant-design/icons'

import { storyCreationApi } from './Create/storyCreationApi'
import type {
  Artifact,
  PackageRecord,
  ProductionRun,
  ProjectIntent,
  StoryBlueprint,
  StoryException,
  StoryProject,
  StoryCreationSettings,
} from './Create/storyCreationTypes'
import './Create/storyCreationWorkspace.css'

const { Content, Sider } = Layout
const { Paragraph, Text, Title } = Typography

interface CreateProps {
  isAuth: boolean
  usersSharedState: { roles?: string[] }
}

type IntentFormValues = Omit<
  ProjectIntent,
  | 'themes'
  | 'required_tropes'
  | 'prose_traits'
  | 'content_boundaries'
  | 'prohibited_content'
  | 'research_requirements'
  | 'automation'
> & {
  themes_text: string
  required_tropes_text: string
  prose_traits_text: string
  content_boundaries_text: string
  prohibited_content_text: string
  research_requirements_text: string
  max_repairs_per_chapter: number
  minimum_gate_score: number
  allow_safe_defaults: boolean
  delivery_hold: boolean
}

const defaultIntentValues: IntentFormValues = {
  project_code: '',
  title_direction: '',
  author_name: '',
  series_name: '',
  book_number: null,
  genre: '',
  subgenre: '',
  intended_audience: 'Adult commercial fiction readers',
  market_promise: '',
  premise: '',
  themes_text: '',
  emotional_experience: '',
  required_tropes_text: '',
  pov: 'Third-person limited',
  tense: 'Past',
  tone: 'Emotionally immersive, polished, and genre-appropriate',
  pacing: 'Balanced with escalating tension',
  prose_traits_text: 'Distinct character voice\nConcrete sensory detail\nPurposeful scenes',
  target_word_count: 70000,
  chapter_count: 24,
  content_boundaries_text: '',
  prohibited_content_text: '',
  all_romantic_characters_are_adults: true,
  consent_policy: 'All romantic and sexual interactions involve adults and affirmative consent.',
  research_requirements_text: '',
  output_targets: ['docx', 'epub', 'txt'],
  model_id: 'GPT6Sol',
  story_model_id: 'GPT6Luna',
  evaluator_model_id: 'GPT6Sol',
  reasoning_effort: 'low',
  maximum_budget_usd: 50,
  max_repairs_per_chapter: 2,
  minimum_gate_score: 0.82,
  allow_safe_defaults: true,
  delivery_hold: false,
}

export const splitList = (value?: string): string[] =>
  String(value || '')
    .split(/\n/)
    .map((item) => item.trim())
    .filter(Boolean)

// List fields are deliberately newline-delimited so punctuation inside a
// generated sentence cannot silently create extra schema items.
export const joinList = (value?: string[]): string => (value || []).join('\n')

const formToIntent = (values: IntentFormValues): ProjectIntent => ({
  project_code: values.project_code,
  title_direction: values.title_direction || '',
  author_name: values.author_name,
  series_name: values.series_name || '',
  book_number: values.book_number || null,
  genre: values.genre,
  subgenre: values.subgenre || '',
  intended_audience: values.intended_audience,
  market_promise: values.market_promise || '',
  premise: values.premise,
  themes: splitList(values.themes_text),
  emotional_experience: values.emotional_experience || '',
  required_tropes: splitList(values.required_tropes_text),
  pov: values.pov,
  tense: values.tense,
  tone: values.tone,
  pacing: values.pacing || 'balanced',
  prose_traits: splitList(values.prose_traits_text),
  target_word_count: values.target_word_count,
  chapter_count: values.chapter_count,
  content_boundaries: splitList(values.content_boundaries_text),
  prohibited_content: splitList(values.prohibited_content_text),
  all_romantic_characters_are_adults: Boolean(values.all_romantic_characters_are_adults),
  consent_policy: values.consent_policy,
  research_requirements: splitList(values.research_requirements_text),
  output_targets: values.output_targets,
  model_id: values.model_id,
  story_model_id: values.story_model_id,
  evaluator_model_id: values.evaluator_model_id || null,
  reasoning_effort: values.reasoning_effort,
  maximum_budget_usd: values.maximum_budget_usd,
  automation: {
    max_repairs_per_chapter: values.max_repairs_per_chapter,
    max_blueprint_repairs: 2,
    minimum_gate_score: values.minimum_gate_score,
    minimum_word_count_ratio: 0.72,
    maximum_word_count_ratio: 1.35,
    allow_safe_defaults: Boolean(values.allow_safe_defaults),
    auto_package: true,
    delivery_hold: Boolean(values.delivery_hold),
  },
})

const intentToForm = (intent: ProjectIntent): IntentFormValues => ({
  ...intent,
  themes_text: joinList(intent.themes),
  required_tropes_text: joinList(intent.required_tropes),
  prose_traits_text: joinList(intent.prose_traits),
  content_boundaries_text: joinList(intent.content_boundaries),
  prohibited_content_text: joinList(intent.prohibited_content),
  research_requirements_text: joinList(intent.research_requirements),
  max_repairs_per_chapter: intent.automation.max_repairs_per_chapter,
  minimum_gate_score: intent.automation.minimum_gate_score,
  allow_safe_defaults: intent.automation.allow_safe_defaults,
  delivery_hold: intent.automation.delivery_hold,
})

export const errorMessage = (error: any): string => {
  const detail = error?.response?.data?.detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail)) {
    const issues = detail
      .map((item) => {
        if (typeof item === 'string') return item
        const location = Array.isArray(item?.loc)
          ? item.loc.filter((part: unknown) => !['body', 'intent'].includes(String(part))).join(' → ')
          : ''
        const messageText = String(item?.msg || item?.message || '').trim()
        return [location, messageText].filter(Boolean).join(': ')
      })
      .filter(Boolean)
    if (issues.length) return `Please correct: ${issues.join('; ')}`
  }
  if (detail && typeof detail === 'object') {
    const nested = detail.message || detail.error || detail.detail
    if (typeof nested === 'string' && nested.trim()) return nested
  }
  return String(error?.message || 'The operation could not be completed.')
}

const statusColor = (status: string): string => {
  if (status === 'completed' || status === 'passing' || status === 'blueprint_ready') return 'green'
  if (status === 'blocked' || status === 'failed') return 'red'
  if (status === 'running' || status === 'repairing' || status === 'queued') return 'blue'
  if (status === 'locked') return 'purple'
  return 'default'
}

const workflowStep = (project?: StoryProject | null): number => {
  if (!project) return 0
  if (project.status === 'draft') return 0
  if (project.status === 'blueprint_ready') return 1
  return 2
}

const IntentForm: React.FC<{
  initialValues: IntentFormValues
  submitting: boolean
  submitLabel: string
  showAssist: boolean
  onSubmit: (values: IntentFormValues) => Promise<void>
}> = ({ initialValues, submitting, submitLabel, showAssist, onSubmit }) => {
  const [form] = Form.useForm<IntentFormValues>()
  const [assisting, setAssisting] = useState(false)
  useEffect(() => form.setFieldsValue(initialValues), [form, initialValues])

  const assistIntent = async () => {
    const seed = form.getFieldsValue(['genre', 'subgenre', 'premise'])
    setAssisting(true)
    try {
      const draft = await storyCreationApi.assistIntent({
        genre: String(seed.genre || '').trim(),
        subgenre: String(seed.subgenre || '').trim(),
        premise: String(seed.premise || '').trim(),
      })
      form.setFieldsValue({
        project_code: draft.project_code,
        title_direction: draft.title_direction,
        series_name: draft.series_name,
        book_number: draft.book_number ?? null,
        genre: draft.genre,
        subgenre: draft.subgenre,
        intended_audience: draft.intended_audience,
        market_promise: draft.market_promise,
        premise: draft.premise,
        themes_text: joinList(draft.themes),
        emotional_experience: draft.emotional_experience,
        required_tropes_text: joinList(draft.required_tropes),
        pov: draft.pov,
        tense: draft.tense,
        tone: draft.tone,
        pacing: draft.pacing,
        prose_traits_text: joinList(draft.prose_traits),
        target_word_count: draft.target_word_count,
        chapter_count: draft.chapter_count,
        content_boundaries_text: joinList(draft.content_boundaries),
        prohibited_content_text: joinList(draft.prohibited_content),
        all_romantic_characters_are_adults: draft.all_romantic_characters_are_adults,
        consent_policy: draft.consent_policy,
        research_requirements_text: joinList(draft.research_requirements),
      })
      message.success('AI generated a complete project brief. Review it, add the author name, then create the project.')
    } catch (error) {
      message.error(errorMessage(error))
    } finally {
      setAssisting(false)
    }
  }

  return (
    <Form form={form} layout="vertical" initialValues={initialValues} onFinish={onSubmit} requiredMark="optional">
      {showAssist && (
        <Alert
          type="info"
          showIcon
          message="Start with an AI-generated project brief"
          description={(
            <Space direction="vertical" size="small">
              <Text>
                Enter a genre, subgenre, and/or premise first if you have them. AI preserves those constraints and fills the remaining creative fields. Nothing is saved until you create the project.
              </Text>
              <Button htmlType="button" icon={<RobotOutlined />} loading={assisting} onClick={assistIntent}>
                Generate project brief with AI
              </Button>
            </Space>
          )}
          style={{ marginBottom: 20 }}
        />
      )}
      <div className="story-form-grid">
        <Form.Item name="project_code" label="Internal project code" rules={[{ required: true }]}>
          <Input placeholder="RAVEN-01" />
        </Form.Item>
        <Form.Item name="author_name" label="Author name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="title_direction" label="Working title or direction">
          <Input />
        </Form.Item>
        <Form.Item name="genre" label="Genre" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="subgenre" label="Subgenre">
          <Input />
        </Form.Item>
        <Form.Item name="intended_audience" label="Intended audience" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="series_name" label="Series name">
          <Input />
        </Form.Item>
        <Form.Item name="book_number" label="Book number">
          <InputNumber min={1} max={999} style={{ width: '100%' }} />
        </Form.Item>
      </div>
      <Form.Item
        name="premise"
        label="Premise"
        rules={[{ required: true }, { min: 20, message: 'Give the planner at least 20 characters.' }]}
      >
        <Input.TextArea rows={4} placeholder="The central character, conflict, stakes, and distinguishing idea." />
      </Form.Item>
      <div className="story-form-grid">
        <Form.Item name="market_promise" label="Market promise">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="emotional_experience" label="Desired emotional experience">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="themes_text" label="Themes" extra="One item per line">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="required_tropes_text" label="Required tropes" extra="One item per line">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="pov" label="POV" rules={[{ required: true }]}>
          <Select options={['First person', 'Third-person limited', 'Dual first person', 'Multiple third-person limited'].map((value) => ({ value, label: value }))} />
        </Form.Item>
        <Form.Item name="tense" label="Tense" rules={[{ required: true }]}>
          <Select options={['Past', 'Present'].map((value) => ({ value, label: value }))} />
        </Form.Item>
        <Form.Item name="tone" label="Tone" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="pacing" label="Pacing">
          <Input />
        </Form.Item>
        <Form.Item name="prose_traits_text" label="Positive prose traits" extra="One item per line">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="research_requirements_text" label="Research requirements" extra="One item per line">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="target_word_count" label="Target words" rules={[{ required: true }]}>
          <InputNumber min={1000} max={250000} step={5000} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="chapter_count" label="Chapters" rules={[{ required: true }]}>
          <InputNumber min={1} max={100} style={{ width: '100%' }} />
        </Form.Item>
      </div>

      <Divider orientation="left">Content boundaries</Divider>
      <Alert
        type="info"
        showIcon
        message="Safety facts are part of the Creative Lock"
        description="Be explicit now so production can proceed without later human review. Ambiguous age, consent, or content limits stop the run."
        style={{ marginBottom: 16 }}
      />
      <div className="story-form-grid">
        <Form.Item name="content_boundaries_text" label="Allowed content and limits" extra="One item per line">
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item name="prohibited_content_text" label="Prohibited content" extra="One item per line">
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item name="consent_policy" label="Consent policy" rules={[{ required: true }]}>
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="all_romantic_characters_are_adults" label="Adult-age confirmation" valuePropName="checked">
          <Checkbox>All romantic or sexual characters are explicitly adults</Checkbox>
        </Form.Item>
      </div>

      <Divider orientation="left">Autonomous production policy</Divider>
      <div className="story-form-grid">
        <Form.Item
          name="model_id"
          label="Orchestration model"
          extra="Blueprints, editorial evaluation, repair planning, and metadata"
          rules={[{ required: true }]}
        >
          <Select options={[{ value: 'GPT6Sol', label: 'ChatGPT-6 Sol' }]} />
        </Form.Item>
        <Form.Item
          name="story_model_id"
          label="Story prose model"
          extra="Chapter drafting and any repair that rewrites manuscript prose"
          rules={[{ required: true }]}
        >
          <Select options={[{ value: 'GPT6Luna', label: 'ChatGPT-6 Luna' }]} />
        </Form.Item>
        <Form.Item name="evaluator_model_id" label="Evaluator model" rules={[{ required: true }]}>
          <Select options={[{ value: 'GPT6Sol', label: 'ChatGPT-6 Sol' }]} />
        </Form.Item>
        <Form.Item name="reasoning_effort" label="Reasoning effort" rules={[{ required: true }]}>
          <Select options={[{ value: 'low', label: 'Low' }]} />
        </Form.Item>
        <Form.Item name="maximum_budget_usd" label="Maximum budget (USD)" rules={[{ required: true }]}>
          <InputNumber min={0.01} max={10000} precision={2} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="max_repairs_per_chapter" label="Automatic repairs per chapter">
          <InputNumber min={0} max={5} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="minimum_gate_score" label="Minimum automated gate score">
          <InputNumber min={0} max={1} step={0.01} precision={2} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="output_targets" label="Automatic output package">
          <Checkbox.Group options={['docx', 'epub', 'txt']} />
        </Form.Item>
        <Form.Item name="allow_safe_defaults" label="Exception behavior" valuePropName="checked">
          <Switch checkedChildren="Safe defaults on" unCheckedChildren="Stop on ambiguity" />
        </Form.Item>
        <Form.Item name="delivery_hold" label="Delivery hold" valuePropName="checked">
          <Switch checkedChildren="Hold package" unCheckedChildren="Auto-package" />
        </Form.Item>
      </div>
      <Button type="primary" htmlType="submit" loading={submitting} icon={<RobotOutlined />}>
        {submitLabel}
      </Button>
    </Form>
  )
}

const BlueprintReview: React.FC<{ blueprint: StoryBlueprint }> = ({ blueprint }) => (
  <Space direction="vertical" size="large" style={{ width: '100%' }}>
    <Descriptions bordered column={{ xs: 1, md: 2 }}>
      <Descriptions.Item label="Working title">{blueprint.working_title}</Descriptions.Item>
      <Descriptions.Item label="Projected words">{blueprint.projected_word_count.toLocaleString()}</Descriptions.Item>
      <Descriptions.Item label="Story promise" span={2}>{blueprint.story_promise}</Descriptions.Item>
      <Descriptions.Item label="Ending" span={2}>{blueprint.ending}</Descriptions.Item>
    </Descriptions>
    <div className="story-stat-grid">
      <Card size="small"><Statistic title="Characters" value={blueprint.characters.length} /></Card>
      <Card size="small"><Statistic title="Locations" value={blueprint.locations.length} /></Card>
      <Card size="small"><Statistic title="Chapters" value={blueprint.chapters.length} /></Card>
      <Card size="small"><Statistic title="Plot milestones" value={blueprint.plot_architecture.length} /></Card>
    </div>
    <Collapse
      items={[
        {
          key: 'characters',
          label: `Principal cast (${blueprint.characters.length})`,
          children: (
            <List
              dataSource={blueprint.characters}
              renderItem={(character) => (
                <List.Item>
                  <List.Item.Meta
                    title={`${character.name} — ${character.role}`}
                    description={`${character.motivation} Arc: ${character.arc}${character.romantic_or_sexual_participant ? ` · Romantic participant age ${character.age ?? 'unverified'}` : ''}`}
                  />
                </List.Item>
              )}
            />
          ),
        },
        {
          key: 'plot',
          label: 'Plot architecture',
          children: <ol>{blueprint.plot_architecture.map((item, index) => <li key={`${index}-${item}`}>{item}</li>)}</ol>,
        },
        {
          key: 'chapters',
          label: `Chapter plan (${blueprint.chapters.length})`,
          children: (
            <List
              dataSource={blueprint.chapters}
              renderItem={(chapter) => (
                <List.Item>
                  <List.Item.Meta
                    title={`Chapter ${chapter.chapter_number}${chapter.title ? `: ${chapter.title}` : ''}`}
                    description={`${chapter.purpose} · ${chapter.target_word_count.toLocaleString()} words · ${chapter.scenes.length} scene(s)`}
                  />
                </List.Item>
              )}
            />
          ),
        },
        {
          key: 'boundaries',
          label: 'Content profile and world rules',
          children: (
            <Space direction="vertical">
              <div>{blueprint.content_profile.map((item) => <Tag key={item}>{item}</Tag>)}</div>
              <ul>{blueprint.world_rules.map((item) => <li key={item}>{item}</li>)}</ul>
            </Space>
          ),
        },
      ]}
    />
  </Space>
)

const Create: React.FC<CreateProps> = ({ isAuth, usersSharedState }) => {
  const isAdmin = isAuth && (usersSharedState.roles || []).map((role) => role.toLowerCase()).includes('admin')
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [runtimeSettings, setRuntimeSettings] = useState<StoryCreationSettings | null>(null)
  const [projects, setProjects] = useState<StoryProject[]>([])
  const [selectedId, setSelectedId] = useState<string>('')
  const [project, setProject] = useState<StoryProject | null>(null)
  const [blueprints, setBlueprints] = useState<Array<Artifact<StoryBlueprint>>>([])
  const [runs, setRuns] = useState<ProductionRun[]>([])
  const [exceptions, setExceptions] = useState<StoryException[]>([])
  const [packages, setPackages] = useState<PackageRecord[]>([])
  const [estimate, setEstimate] = useState<{ estimatedCostUsd: number; maximumBudgetUsd: number; withinBudget: boolean } | null>(null)
  const [loading, setLoading] = useState(true)
  const [action, setAction] = useState('')
  const [intentOpen, setIntentOpen] = useState(false)
  const [revisionInstruction, setRevisionInstruction] = useState('')
  const [exceptionResolution, setExceptionResolution] = useState<Record<string, string>>({})

  const currentBlueprint = useMemo(() => {
    const currentId = project?.current_versions?.blueprint
    return blueprints.find((item) => item.artifact_id === currentId) || [...blueprints].sort((a, b) => b.version - a.version)[0]
  }, [blueprints, project])
  const currentRun = useMemo(() => [...runs].sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))[0], [runs])
  const currentRunLeaseExpired = Boolean(
    currentRun?.lease_expires_at &&
      ['running', 'repairing'].includes(currentRun.status) &&
      Date.parse(currentRun.lease_expires_at) <= Date.now(),
  )
  const openExceptions = useMemo(() => exceptions.filter((item) => item.status === 'open'), [exceptions])

  const refreshProject = useCallback(async (projectId: string, quiet = false) => {
    if (!projectId) return
    if (!quiet) setLoading(true)
    try {
      const [nextProject, nextBlueprints, nextRuns, nextExceptions, nextPackages, nextEstimate] = await Promise.all([
        storyCreationApi.getProject(projectId),
        storyCreationApi.listBlueprints(projectId),
        storyCreationApi.listRuns(projectId),
        storyCreationApi.listExceptions(projectId),
        storyCreationApi.listPackages(projectId),
        storyCreationApi.getEstimate(projectId),
      ])
      setProject(nextProject)
      setBlueprints(nextBlueprints)
      setRuns(nextRuns)
      setExceptions(nextExceptions)
      setPackages(nextPackages)
      setEstimate(nextEstimate)
      setProjects((items) => items.map((item) => (item.project_id === nextProject.project_id ? nextProject : item)))
    } catch (error) {
      if (!quiet) message.error(errorMessage(error))
    } finally {
      if (!quiet) setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false)
      return
    }
    let active = true
    ;(async () => {
      try {
        const settings = await storyCreationApi.settings()
        if (!active) return
        setRuntimeSettings(settings)
        setEnabled(settings.enabled)
        if (!settings.enabled) return
        const items = await storyCreationApi.listProjects()
        if (!active) return
        setProjects(items)
        const first = items[0]?.project_id || ''
        setSelectedId(first)
        if (first) await refreshProject(first)
      } catch (error) {
        if (active) message.error(errorMessage(error))
      } finally {
        if (active) setLoading(false)
      }
    })()
    return () => {
      active = false
    }
  }, [isAdmin, refreshProject])

  useEffect(() => {
    if (!selectedId) {
      setProject(null)
      setBlueprints([])
      setRuns([])
      setExceptions([])
      setPackages([])
      setEstimate(null)
      return
    }
    void refreshProject(selectedId)
  }, [selectedId, refreshProject])

  useEffect(() => {
    if (!selectedId || !currentRun || !['queued', 'running', 'repairing'].includes(currentRun.status)) return
    const timer = window.setInterval(() => void refreshProject(selectedId, true), 4000)
    return () => window.clearInterval(timer)
  }, [currentRun, refreshProject, selectedId])

  const createOrUpdateIntent = async (values: IntentFormValues) => {
    setAction('intent')
    try {
      const intent = formToIntent(values)
      if (project && intentOpen) {
        const updated = await storyCreationApi.updateIntent(project.project_id, intent, project.revision)
        setProject(updated)
        await refreshProject(updated.project_id)
        message.success('Intent updated. Downstream versions were preserved and marked stale where required.')
      } else {
        const created = await storyCreationApi.createProject(intent)
        setProjects((items) => [created, ...items])
        setSelectedId(created.project_id)
        message.success('Project created. AI can now build the complete blueprint.')
      }
      setIntentOpen(false)
    } catch (error) {
      message.error(errorMessage(error))
    } finally {
      setAction('')
    }
  }

  const generateBlueprint = async () => {
    if (!project) return
    setAction('blueprint')
    try {
      await storyCreationApi.generateBlueprint(project.project_id)
      await refreshProject(project.project_id)
      message.success('Blueprint generated and passed automated planning validation.')
    } catch (error) {
      message.error(errorMessage(error))
    } finally {
      setAction('')
    }
  }

  const archiveProject = async () => {
    if (!project) return
    setAction('archive')
    try {
      await storyCreationApi.archiveProject(project.project_id, project.revision)
      const remaining = projects.filter((item) => item.project_id !== project.project_id)
      setProjects(remaining)
      setProject(null)
      setSelectedId(remaining[0]?.project_id || '')
      message.success('Project archived. Its version and audit history were preserved.')
    } catch (error) {
      message.error(errorMessage(error))
    } finally {
      setAction('')
    }
  }

  const reviseBlueprint = async () => {
    if (!project || !revisionInstruction.trim()) return
    setAction('revise')
    try {
      await storyCreationApi.reviseBlueprint(project.project_id, revisionInstruction, project.revision)
      setRevisionInstruction('')
      await refreshProject(project.project_id)
      message.success('AI revised and revalidated the blueprint.')
    } catch (error) {
      message.error(errorMessage(error))
    } finally {
      setAction('')
    }
  }

  const lockAndLaunch = async () => {
    if (!project || !currentBlueprint) return
    setAction('launch')
    try {
      let locked = project
      if (!project.creative_lock) {
        locked = await storyCreationApi.createCreativeLock(project.project_id, currentBlueprint.artifact_id)
      }
      await storyCreationApi.startRun(locked.project_id, `${locked.project_id}-${Date.now()}`)
      await refreshProject(locked.project_id)
      message.success('Creative Lock created. Autonomous production is running.')
    } catch (error) {
      message.error(errorMessage(error))
    } finally {
      setAction('')
    }
  }

  const actOnRun = async (nextAction: 'pause' | 'resume') => {
    if (!project || !currentRun) return
    setAction(nextAction)
    try {
      await storyCreationApi.runAction(project.project_id, currentRun.run_id, nextAction)
      await refreshProject(project.project_id)
      message.success(`Run ${nextAction} request accepted.`)
    } catch (error) {
      message.error(errorMessage(error))
    } finally {
      setAction('')
    }
  }

  const resolveException = async (item: StoryException) => {
    if (!project) return
    const resolution = (exceptionResolution[item.exception_id] || item.safe_default || '').trim()
    if (!resolution) {
      message.warning('Provide the narrow decision needed to resume production.')
      return
    }
    setAction(`exception-${item.exception_id}`)
    try {
      await storyCreationApi.resolveException(project.project_id, item.exception_id, resolution)
      await refreshProject(project.project_id)
      message.success('Exception resolved. Autonomous production resumed from saved state.')
    } catch (error) {
      message.error(errorMessage(error))
    } finally {
      setAction('')
    }
  }

  if (!isAdmin) {
    return <Result status="403" title="Internal access only" subTitle="Book creation is restricted to administrators." />
  }
  if (loading && enabled === null) {
    return <div className="story-workspace-loading"><Spin size="large" tip="Loading internal book production…" /></div>
  }
  if (enabled === false) {
    return (
      <Result
        status="warning"
        title="Book production is disabled"
        subTitle="The server kill switch is off. Set STORY_CREATION_ENABLED only in an authorized internal environment."
      />
    )
  }

  return (
    <div className="story-workspace-page">
      <div className="story-workspace-header">
        <div>
          <Text className="story-workspace-kicker">INTERNAL · ADMIN ONLY</Text>
          <Title level={2}>Autonomous Book Production</Title>
          <Paragraph>Set direction once, approve the Creative Lock, and let AI draft, evaluate, repair, and package the manuscript.</Paragraph>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setProject(null); setIntentOpen(true) }}>
          New project
        </Button>
      </div>

      <Alert
        type="info"
        showIcon
        message="Human-directed, AI-operated"
        description="After Creative Lock, routine chapter, metadata, and package approvals are intentionally absent. Only blocking exceptions return here."
        style={{ marginBottom: 16 }}
      />

      {runtimeSettings?.aiProviderMode === 'local_demo' && (
        <Alert
          type="warning"
          showIcon
          message="Local sandbox mode"
          description="Projects exist only in backend memory and reset when the backend restarts. AI output is deterministic test content; this mode makes no Firestore story writes and no paid model calls."
          style={{ marginBottom: 16 }}
        />
      )}

      {runtimeSettings?.ephemeral && runtimeSettings?.aiProviderMode !== 'local_demo' && (
        <Alert
          type="warning"
          showIcon
          message="Live-model local sandbox"
          description="Projects reset when the backend restarts and no story or usage records are written to Firestore. Terra and Luna calls are live, billable OpenAI API requests."
          style={{ marginBottom: 16 }}
        />
      )}

      <Layout className="story-workspace-layout">
        <Sider width={280} theme="light" className="story-project-sider">
          <div className="story-sider-title">Projects</div>
          <List
            dataSource={projects.filter((item) => item.status !== 'archived')}
            locale={{ emptyText: 'No story projects yet' }}
            renderItem={(item) => (
              <List.Item
                className={item.project_id === selectedId ? 'story-project-row story-project-row--active' : 'story-project-row'}
                onClick={() => setSelectedId(item.project_id)}
              >
                <List.Item.Meta
                  title={item.intent.title_direction || item.intent.project_code}
                  description={<Space size={4}><Tag color={statusColor(item.status)}>{item.status}</Tag><Text type="secondary">r{item.revision}</Text></Space>}
                />
              </List.Item>
            )}
          />
        </Sider>
        <Content className="story-workspace-content">
          {!project ? (
            <Empty description="Create or select an internal story project">
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setIntentOpen(true)}>Create project</Button>
            </Empty>
          ) : (
            <Spin spinning={loading}>
              <Steps
                current={workflowStep(project)}
                items={[
                  { title: 'Project intent', description: 'Human direction' },
                  { title: 'Creative Lock', description: 'One review' },
                  { title: 'Autonomous production', description: 'AI-operated' },
                ]}
                style={{ marginBottom: 28 }}
              />

              <Card
                title={<Space><span>{project.intent.title_direction || project.intent.project_code}</span><Tag color={statusColor(project.status)}>{project.status}</Tag></Space>}
                extra={
                  <Space>
                    {!project.creative_lock && <Button onClick={() => setIntentOpen(true)}>Edit intent</Button>}
                    <Button
                      danger
                      loading={action === 'archive'}
                      onClick={() => void Modal.confirm({
                        title: 'Archive this project?',
                        content: 'Artifacts and audit history are preserved. Active runs must be paused first.',
                        okText: 'Archive',
                        okButtonProps: { danger: true },
                        onOk: archiveProject,
                      })}
                    >
                      Archive
                    </Button>
                  </Space>
                }
              >
                <Descriptions column={{ xs: 1, md: 3 }}>
                  <Descriptions.Item label="Genre">{project.intent.genre}{project.intent.subgenre ? ` / ${project.intent.subgenre}` : ''}</Descriptions.Item>
                  <Descriptions.Item label="Target">{project.intent.target_word_count.toLocaleString()} words</Descriptions.Item>
                  <Descriptions.Item label="Budget">
                    {estimate ? (
                      <Space size={4}>
                        <span>${estimate.estimatedCostUsd.toFixed(2)} estimate / ${estimate.maximumBudgetUsd.toFixed(2)} max</span>
                        <Tag color={estimate.withinBudget ? 'green' : 'red'}>{estimate.withinBudget ? 'within budget' : 'blocked'}</Tag>
                      </Space>
                    ) : `$${project.intent.maximum_budget_usd.toFixed(2)}`}
                  </Descriptions.Item>
                  <Descriptions.Item label="POV / tense">{project.intent.pov} / {project.intent.tense}</Descriptions.Item>
                  <Descriptions.Item label="Orchestration">{project.intent.model_id} · {project.intent.reasoning_effort}</Descriptions.Item>
                  <Descriptions.Item label="Story prose">{project.intent.story_model_id}</Descriptions.Item>
                  <Descriptions.Item label="Repairs">Up to {project.intent.automation.max_repairs_per_chapter} per chapter</Descriptions.Item>
                </Descriptions>
                <Paragraph style={{ marginTop: 12 }}>{project.intent.premise}</Paragraph>
              </Card>

              <Divider />
              {!currentBlueprint ? (
                <Card title="AI story blueprint">
                  <Paragraph>AI will create the story bible, cast, locations, plot architecture, chapter plan, continuity obligations, projected length, and planning QA in one structured run.</Paragraph>
                  <Button type="primary" icon={<RobotOutlined />} loading={action === 'blueprint'} onClick={generateBlueprint}>
                    Generate and validate blueprint
                  </Button>
                </Card>
              ) : (
                <Card
                  title={<Space><span>Consolidated blueprint review</span><Tag color={statusColor(currentBlueprint.status)}>v{currentBlueprint.version} · {currentBlueprint.status}</Tag></Space>}
                >
                  <BlueprintReview blueprint={currentBlueprint.content} />
                  {!project.creative_lock && (
                    <>
                      <Divider />
                      <Title level={4}>One revision instruction, if needed</Title>
                      <Input.TextArea
                        rows={3}
                        value={revisionInstruction}
                        onChange={(event) => setRevisionInstruction(event.target.value)}
                        placeholder="Example: Keep the ending, but make the midpoint reversal originate from the protagonist's choice."
                      />
                      <Space wrap style={{ marginTop: 12 }}>
                        <Button loading={action === 'revise'} disabled={!revisionInstruction.trim()} onClick={reviseBlueprint}>
                          Revise and revalidate
                        </Button>
                        <Button
                          type="primary"
                          icon={<LockOutlined />}
                          loading={action === 'launch'}
                          onClick={() => void Modal.confirm({
                            title: 'Create the Creative Lock and launch autonomous production?',
                            content: 'This is the final routine human approval. AI will draft, evaluate, repair, create metadata, and package passing output automatically.',
                            okText: 'Lock and launch',
                            onOk: lockAndLaunch,
                          })}
                        >
                          Approve Creative Lock & launch
                        </Button>
                      </Space>
                    </>
                  )}
                </Card>
              )}

              {project.creative_lock && (
                <>
                  <Divider />
                  <Card title={<Space><RobotOutlined /> Autonomous production</Space>}>
                    {!currentRun ? (
                      <Button type="primary" icon={<PlayCircleOutlined />} loading={action === 'launch'} onClick={lockAndLaunch}>
                        Start production run
                      </Button>
                    ) : (
                      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <div className="story-run-heading">
                          <Space><Tag color={statusColor(currentRun.status)}>{currentRun.status}</Tag><Text>{currentRun.progress.current_stage.replace(/_/g, ' ')}</Text></Space>
                          <Text type="secondary">Estimated ${currentRun.estimated_cost_usd.toFixed(2)}</Text>
                        </div>
                        <Progress
                          percent={Math.round(currentRun.progress.percent)}
                          status={currentRun.status === 'blocked' || currentRun.status === 'failed' ? 'exception' : currentRun.status === 'completed' ? 'success' : 'active'}
                        />
                        <Text>
                          {currentRun.progress.completed_chapters} of {currentRun.progress.total_chapters} chapters machine-accepted
                          {currentRun.progress.current_chapter ? ` · working on chapter ${currentRun.progress.current_chapter}` : ''}
                        </Text>
                        {currentRun.error && <Alert type="error" showIcon message={currentRun.error} />}
                        <Space wrap>
                          {['queued', 'running', 'repairing'].includes(currentRun.status) && (
                            <Button icon={<PauseCircleOutlined />} loading={action === 'pause'} onClick={() => actOnRun('pause')}>Pause</Button>
                          )}
                          {(['paused', 'failed'].includes(currentRun.status) || currentRunLeaseExpired) && (
                            <Button type="primary" icon={<PlayCircleOutlined />} loading={action === 'resume'} onClick={() => actOnRun('resume')}>
                              {currentRunLeaseExpired ? 'Recover stale run' : 'Resume'}
                            </Button>
                          )}

                        </Space>
                      </Space>
                    )}
                  </Card>
                </>
              )}

              {openExceptions.length > 0 && (
                <>
                  <Divider />
                  <Alert type="warning" showIcon message={`${openExceptions.length} narrow human decision${openExceptions.length === 1 ? '' : 's'} required`} />
                  <List
                    className="story-exception-list"
                    dataSource={openExceptions}
                    renderItem={(item) => (
                      <List.Item>
                        <Card style={{ width: '100%' }} title={<Space><Tag color="orange">{item.exception_type}</Tag>{item.question}</Space>}>
                          <Paragraph><strong>AI recommendation:</strong> {item.recommended_answer}</Paragraph>
                          <Paragraph type="secondary">{item.downstream_effect}</Paragraph>
                          <Input.TextArea
                            rows={2}
                            value={exceptionResolution[item.exception_id] ?? item.safe_default ?? ''}
                            onChange={(event) => setExceptionResolution((values) => ({ ...values, [item.exception_id]: event.target.value }))}
                          />
                          <Button
                            type="primary"
                            style={{ marginTop: 12 }}
                            loading={action === `exception-${item.exception_id}`}
                            onClick={() => resolveException(item)}
                          >
                            Resolve and resume
                          </Button>
                        </Card>
                      </List.Item>
                    )}
                  />
                </>
              )}

              {packages.length > 0 && (
                <>
                  <Divider />
                  <Card title={<Space><CheckCircleOutlined style={{ color: '#52c41a' }} /> Machine-approved release packages</Space>}>
                    <List
                      dataSource={packages}
                      renderItem={(item) => (
                        <List.Item
                          actions={item.formats.map((format) => (
                            <Button key={format} icon={<CloudDownloadOutlined />} onClick={() => storyCreationApi.downloadPackage(project.project_id, item.package_id, format)}>
                              {format.toUpperCase()}
                            </Button>
                          ))}
                        >
                          <List.Item.Meta
                            title={`Package ${item.package_id.slice(-8)}`}
                            description={<Space><Tag color={item.preflight_passed ? 'green' : 'red'}>{item.preflight_passed ? 'preflight passed' : 'blocked'}</Tag><Text type="secondary">{new Date(item.created_at).toLocaleString()}</Text></Space>}
                          />
                        </List.Item>
                      )}
                    />
                  </Card>
                </>
              )}
            </Spin>
          )}
        </Content>
      </Layout>

      <Drawer
        title={project ? 'Edit project intent' : 'New story project'}
        width={Math.min(window.innerWidth * 0.92, 980)}
        open={intentOpen}
        onClose={() => setIntentOpen(false)}
        destroyOnClose
      >
        <IntentForm
          initialValues={project ? intentToForm(project.intent) : defaultIntentValues}
          submitting={action === 'intent'}
          submitLabel={project ? 'Save intent and rebase dependencies' : 'Create project'}
          showAssist={!project}
          onSubmit={createOrUpdateIntent}
        />
      </Drawer>
    </div>
  )
}

export default Create
